//hooks/useWebSocket.js
import { useEffect, useRef, useCallback } from 'react';
import { forceLogoutAndRedirect, attemptTokenRefresh } from '../services/apiClient';

const RECONNECT_INTERVAL = 3000;
const MAX_RECONNECT_ATTEMPTS = 10;
const PING_INTERVAL = 30000;
const PONG_TIMEOUT = 10000;
const CONNECTION_TIMEOUT = 5000;

export const useWebSocket = (
  currentUser,
  isAuthenticated,
  onMessage,
  onConnectionChange,
  onReconnectAttempt,
  onAuthFailure
) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pongTimeoutRef = useRef(null);
  const isReconnectingRef = useRef(false);
  const connectionTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const authenticatedTokenRef = useRef(null);
  const BASE_URL = import.meta.env.VITE_BASE_URL;

  const cleanupWebSocket = useCallback(() => {
    console.log('🧹 Cleaning up WebSocket connections...');

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.onopen = null;
      if (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
    isReconnectingRef.current = false;
  }, []);

  const startPingInterval = useCallback(() => {
    if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);

    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log('📤 Sending ping to server...');
        try {
          wsRef.current.send(JSON.stringify({ type: 'ping' }));
          if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
          pongTimeoutRef.current = setTimeout(() => {
            console.warn('⚠️ No pong received, connection may be stale');
            onConnectionChange('reconnecting');
            wsRef.current?.close();
          }, PONG_TIMEOUT);
        } catch (error) {
          console.error('❌ Error sending ping:', error);
          onConnectionChange('error');
          wsRef.current?.close();
        }
      }
    }, PING_INTERVAL);
  }, [onConnectionChange]);

  // ─── Try refresh → reconnect before giving up ───
  const tryRefreshAndReconnect = useCallback(async () => {
    try {
      await attemptTokenRefresh();
      console.info('✅ Token refreshed after WS auth failure — reconnecting...');
      setTimeout(() => connectWebSocket(), 500);
      return true;
    } catch (e) {
      console.error('❌ Refresh failed during WS auth recovery:', e.message);
      return false;
    }
  }, []); // connectWebSocket added below via ref

  const connectWebSocket = useCallback(() => {
    if (isReconnectingRef.current) {
      console.log('⏳ Reconnection already in progress...');
      return;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already connected');
      return;
    }

    isReconnectingRef.current = true;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.onopen = null;
      if (wsRef.current.readyState === WebSocket.OPEN) wsRef.current.close();
      wsRef.current = null;
    }

    try {
      // Auth happens via the httpOnly cookie sent on the WS upgrade — no token in URL.
      const wsUrl = window.location.hostname === 'localhost'
        ? `ws://localhost:7080/ws`
        : `wss://${window.location.host}/ws`;

      console.log(`🔌 Connecting to WebSocket (Attempt ${reconnectAttemptsRef.current + 1})`);
      onConnectionChange('connecting');

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      connectionTimeoutRef.current = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          console.error('⏱️ Connection timeout');
          ws.close();
          onConnectionChange('timeout');
        }
      }, CONNECTION_TIMEOUT);

      ws.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }

        onConnectionChange('connected');
        reconnectAttemptsRef.current = 0;
        onReconnectAttempt(0);
        isReconnectingRef.current = false;

        // Server already authenticated us at upgrade via cookie; this message
        // just lets the handler emit the legacy 'authenticated' ack.
        ws.send(JSON.stringify({
          type: 'authenticate',
          username: currentUser.username,
        }));

        startPingInterval();
      };

      ws.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('📨 Received message type:', message.type);

          if (message.type === 'pong') {
            console.log('📥 Received pong from server');
            if (pongTimeoutRef.current) {
              clearTimeout(pongTimeoutRef.current);
              pongTimeoutRef.current = null;
            }
            return;
          }

          // ─── Auth failure: try refresh before logout ───
          if (
            message.type === 'auth_failed' ||
            message.type === 'auth_error' ||
            message.type === 'unauthorized' ||
            message.type === 'token_expired' ||
            message.type === 'session_expired'
          ) {
            console.warn('🔄 WebSocket auth failure — attempting refresh...');
            cleanupWebSocket();

            const refreshed = await tryRefreshAndReconnect();
            if (!refreshed && onAuthFailure) onAuthFailure();
            return;
          }

          onMessage(message);
        } catch (error) {
          console.error('❌ Error parsing message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        onConnectionChange('error');
      };

      ws.onclose = async (event) => {
        console.log(`❌ WebSocket closed (Code: ${event.code}, Reason: ${event.reason})`);
        onConnectionChange('disconnected');
        isReconnectingRef.current = false;

        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Auth-related close codes — try refresh before logout
        if (event.code === 4401 || event.code === 4403 || event.code === 1008) {
          console.warn('🔄 WS closed with auth code — attempting refresh...');
          const refreshed = await tryRefreshAndReconnect();
          if (!refreshed && onAuthFailure) onAuthFailure();
          return;
        }

        // Normal reconnect logic
        if (isAuthenticated && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(RECONNECT_INTERVAL * Math.pow(1.5, reconnectAttemptsRef.current), 30000);
          console.log(`🔄 Reconnecting in ${delay}ms...`);
          onConnectionChange('reconnecting');
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            onReconnectAttempt(reconnectAttemptsRef.current);
            connectWebSocket();
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          console.error('❌ Max reconnection attempts reached');
          onConnectionChange('failed');
        }
      };

    } catch (error) {
      console.error('❌ Error creating WebSocket:', error);
      onConnectionChange('error');
      isReconnectingRef.current = false;
    }
  }, [currentUser, isAuthenticated, startPingInterval, onMessage, onConnectionChange, onReconnectAttempt, onAuthFailure, cleanupWebSocket, tryRefreshAndReconnect]);

  // ─── On token refresh, the underlying cookie changes. Reconnect so the
  //     new cookie is used on the next WS upgrade. ───
  useEffect(() => {
    const handleTokenRefreshed = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log('🔑 Token refreshed — reconnecting WebSocket with new cookie');
        wsRef.current.close();
      }
    };

    window.addEventListener('tokenRefreshed', handleTokenRefreshed);
    return () => window.removeEventListener('tokenRefreshed', handleTokenRefreshed);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (isAuthenticated && currentUser) connectWebSocket();
    return () => cleanupWebSocket();
  }, [isAuthenticated, currentUser, connectWebSocket, cleanupWebSocket]);

  return {
    sendMessage,
    connectWebSocket,
    cleanupWebSocket,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    MAX_RECONNECT_ATTEMPTS
  };
};